<template>
    <v-tab-item :value="$route.path" class="pa-3">
        <v-toolbar flat height="90">
            <v-btn
                :disabled="!getPermission('stores.financial_limits.update')"
                color="secondary"
                class="text-capitalize px-5"
                depressed
                v-text="translations.generalLimits[lang]"
                @click="openGeneralModal()"
            ></v-btn>

            <v-spacer></v-spacer>

            <v-col cols="12" sm="4" class="pa-0">
                <b-search-field
                    v-model="search"
                    :selectedSearchType="selectedSearchType"
                    :searchTypes="searchTypes"
                ></b-search-field>
            </v-col>
        </v-toolbar>

        <v-data-table
            :headers="headers"
            :items="dailyLimits"
            :footer-props="{
                itemsPerPageOptions: [12],
                showCurrentPage: true,
                pageText: `${page * 12 - 11}-${
                    page * 12 <= serverItemsLength
                        ? page * 12
                        : serverItemsLength
                } ${translations.paginationText[lang]} ${serverItemsLength}`,
            }"
            :page.sync="page"
            :server-items-length="serverItemsLength"
            :loading="loading"
            disable-sort
            class="b-outlined"
        >
            <template v-slot:item.app_name="{ item }">
                {{ item.store_id + " " + item.app_name }}
            </template>

            <template v-slot:item.online_limits="{ item }">
                <v-btn
                    :disabled="!getPermission('stores.financial_limits.update')"
                    color="secondary"
                    class="px-7"
                    small
                    depressed
                    @click="openModal(item, 'online')"
                >
                    {{
                        item.daily_limits.online.amount
                            ? new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "EUR",
                                  minimumFractionDigits: 2,
                              }).format(item.daily_limits.online.amount)
                            : item.daily_limits.online.points
                    }}
                </v-btn>
            </template>

            <template v-slot:item.offline_limits="{ item }">
                <v-btn
                    :disabled="!getPermission('stores.financial_limits.update')"
                    color="secondary"
                    class="px-7"
                    small
                    depressed
                    @click="openModal(item, 'offline')"
                >
                    {{
                        item.daily_limits.offline.amount
                            ? new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "EUR",
                                  minimumFractionDigits: 2,
                              }).format(item.daily_limits.offline.amount)
                            : item.daily_limits.offline.points
                    }}
                </v-btn>
            </template>
        </v-data-table>

        <v-dialog v-model="updateDialog" max-width="600" scrollable>
            <LimitsForm
                :mode="mode"
                :updateStatus="updateStatus"
                @cancel="updateDialog = false"
            />
        </v-dialog>
    </v-tab-item>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import debounce from "lodash/debounce";
import LimitsForm from "@/components/loyaltyPanel/stores/LimitsForm.vue";
import translations from "@/utils/translations/loyaltyPanel/stores/dailyTransactionLimits";

export default {
    name: "DailyTransactionLimits",

    components: { LimitsForm },

    mixins: [translations],

    data() {
        return {
            page: +this.$route.query.page,
            mode: 0,
            updateStatus: "",
            searchTypes: [
                "All Fields",
                "Name",
                "Name of Shop(in app)",
                "Customer",
                "Transaction",
                "Total",
                "Points",
            ],
            selectedSearchType: "All Fields",
            search: "",
        };
    },

    computed: {
        ...mapState(["loading", "errorMessage", "serverItemsLength"]),
        ...mapState("loyaltyPanel/stores/dailyLimits", ["dailyLimits"]),

        updateDialog: {
            get() {
                return this.$store.state.loyaltyPanel.stores.dailyLimits
                    .updateDialog;
            },
            set(val) {
                this.setDialog(val);
            },
        },

        lang() {
            return this.$route.params.lang;
        },

        headers() {
            return [
                {
                    text: this.translations.name[this.lang],
                    value: `app_name`,
                    width: "34%",
                },
                {
                    text: this.translations.onlineTransactions[this.lang],
                    value: "online_limits",
                    width: "33%",
                },
                {
                    text: this.translations.offlineTransactions[this.lang],
                    value: "offline_limits",
                    width: "33%",
                },
            ];
        },

        query() {
            let query = "?";

            for (let key in this.$route.query) {
                query += `${key}=${this.$route.query[key]}&`;
            }

            return query.slice(0, query.length - 1);
        },
    },

    methods: {
        ...mapActions("loyaltyPanel/stores/dailyLimits", ["getItems"]),
        ...mapMutations("loyaltyPanel/stores/dailyLimits", [
            "setItem",
            "setDialog",
        ]),
        ...mapActions(["getPermission"]),

        openModal(item, status) {
            this.mode = 2;
            this.setDialog(true);
            const body = {
                app_name: item.app_name,
                offline: item.daily_limits.offline,
                online: item.daily_limits.online,
                store_id: item.store_id,
            };
            this.setItem(body);
            this.updateStatus = status;
        },

        openGeneralModal(item, status) {
            this.mode = 1;
            this.setItem({});
            this.setDialog(true);
        },

        handleSearch() {
            this.getItems(`?q=${this.search}`);
        },
    },

    watch: {
        $route(val) {
            if (!val.query.page) {
                this.$router.push({
                    query: {
                        page: 1,
                        ...this.$route.query,
                    },
                });
            }
            this.getItems(this.query);
        },

        page(page) {
            this.$router.push({ query: { ...this.$route.query, page } });
        },

        search(val) {
            if (val == null) {
                this.getItems(this.query);
            } else {
                this.debouncedSearch();
            }
        },
    },

    beforeCreate() {
        if (!this.$route.query.page) {
            this.$router.push({
                query: {
                    page: 1,
                    ...this.$route.query,
                },
            });
        }
    },

    mounted() {
        this.getItems(this.query);
    },

    created() {
        this.debouncedSearch = debounce(this.handleSearch, 500);
    },
};
</script>

<style>
.v-data-footer {
    padding: 12px 0;
}
</style>
